<template>
  <div class="VideoEmbed">
    <video-js
      ref="player"
      :setup="videoSetup"
      :source="video.video_path"
      :resolutions="video.resolutions"
      :turn-on-sound-button="videoSetup.autoplay && videoSetup.muted !== false"
      :download-options="downloadOptions"
      :subtitle-path="video.switches.show_captions ? video.subtitle_url : null"
      :branding-url="brandingUrl"
      :resume-playback-id="`embed-video:${video.canonical}`"
      :should-hide-player-control-bar="noControls"
      default-res="720"
      @ready="onPlayReady"
    />
    <div
      v-if="!noCta"
      ref="ctaRef"
      class="cta-mask"
    >
      <div>
        <template v-for="item in video.call_to_actions">
          <a
            v-if="constants.App_Models_CallToAction.EMBED_SUPPORTED_TYPES.includes(item.type)"
            class="btn-default cta-btn"
            :href="item.url"
            :target="item.url_target"
          >
            {{ item.button_text }}
          </a>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import $ from 'jquery'
import URI from 'urijs'
import TrackVideoWatchMixin from './track-video-watch-mixin'

export default {
  name: 'VideoEmbed',
  mixins: [TrackVideoWatchMixin],
  props: {
    hasCtaFeature: {
      type: Boolean,
      default: false,
    },
    video: {},
    brandingUrl: {},
  },
  data() {
    return {
      player: null,
    }
  },
  computed: {
    urlParams() {
      return URI(window.location.href).search(true)
    },
    noCta() {
      return this.urlParams.no_cta === '1' || this.video.call_to_actions.length === 0 || !this.hasCtaFeature
    },
    noControls() {
      return this.urlParams.no_controls === '1'
    },
    noTrack() {
      return this.urlParams.track === '0'
    },
    downloadOptions() {
      const options = {}

      if (this.video.switches.downloadable) {
        options.videoUrl = this.video.mp4_url
      }

      if (this.video.switches.allow_download_transcript) {
        options.transcriptUrl = this.router('api.video.subtitle.download', { video: this.video.id, format: 'txt' })
      }

      return options
    },
    videoSetup() {
      let setup = {
        fluid: true,
        controls: true,
        poster: this.video.preview_path,
        autoplay: typeof this.urlParams.autoplay !== 'undefined' ? this.urlParams.autoplay === '1' : this.video.switches.video_auto,
      }

      if (setup.autoplay && this.urlParams.muted === '1') {
        setup.muted = true
      }

      if (typeof this.urlParams.width !== 'undefined' && typeof this.urlParams.height !== 'undefined') {
        setup.width = this.urlParams.width
        setup.height = this.urlParams.height
      }

      if (typeof this.urlParams.aspect_ratio !== 'undefined') {
        setup.aspectRatio = this.urlParams.aspect_ratio
      }

      if (!setup.width || setup.width === 'auto') {
        const maxWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
        let ratio = 16 / 9

        if (this.video.video_resolution && this.video.video_resolution.height && this.video.video_resolution.height > 0) {
          ratio = this.video.video_resolution.width / this.video.video_resolution.height
        }

        setup.width = maxWidth
        setup.height = maxWidth / ratio

        window.onresize = () => {
          if (this.player) {
            this.player.width(maxWidth)
            this.player.height(maxWidth / ratio)
          }
        }
      }
      if (setup.autoplay) {
        setup.inactivityTimeout = 0
      }
      else {
        delete setup.muted
      }

      if (this.video.is_youtube) {
        setup = Object.assign(setup, {
          techOrder: ['youtube'],
          sources: [{
            type: 'video/youtube',
            src: `https://www.youtube.com/watch?v=${this.video.video}`,
          }],
          youtube: {
            rel: 0,
            autoplay: setup.autoplay ? 1 : 0,
            ytControls: 0,
          },
        })
      }

      return setup
    },
  },
  mounted() {
    this.trackView()
    $(window).on('unload', () => {
      this.trackWatch()
    })
  },
  methods: {
    onPlayReady(player) {
      this.player = player

      if (this.$refs.ctaRef) {
        this.player.el().appendChild(this.$refs.ctaRef)
      }

      this.player.on('ended', () => {
        if (!this.noCta && this.video.call_to_actions.length > 0) {
          $(this.$refs.ctaRef).css('display', 'flex')
        }
        this.trackWatch()
      })
      this.player.on('loadedmetadata', () => {
        this.videoReady = true
        $(this.$el).find('.video-js').removeClass('vjs-seeking')
      })
      this.player.on('play', () => {
        this.cancelPausedVideoWatch()
        this.player.options({ inactivityTimeout: 2000 })
        if (!this.videoReady) {
          $(this.$el).find('.video-js').removeClass('vjs-seeking')
        }
        if (!this.noCta && this.video.call_to_actions.length > 0) {
          $(this.$refs.ctaRef).css('display', 'none')
        }
      })
      this.player.on('pause', () => {
        this.trackPausedVideoWatch(this.player, () => {
          this.trackWatch()
        })
      })
      this.player.on('error', () => {
        this.player.addClass('vjs-waiting')
        this.player.removeClass('vjs-paused')
      })
    },
    trackView() {
      if (this.noTrack) {
        return
      }
      axios.post(this.router('api.video.track', { video: this.video.id, from_embed: 1 }), {
        type: this.constants.App_Models_Track.ACTION_VIEW,
      }, {
        ignoreGlobalCatch: true,
      })
    },
    trackWatch() {
      if (this.noTrack) {
        return
      }
      this.smartTrackVideoWatch(this.video, this.player, {})
    },
  },
}
</script>

<style scoped>
.VideoEmbed :deep(.vjs-ended:not(.vjs-vr) .vjs-control-bar) {
  display: flex;
}
</style>
